import type { WhiteboardTranslations } from "./translations";

export const ZH_CN_TRANSLATIONS: WhiteboardTranslations = {
  workspace: {
    cancelLabel: "取消",
    syncingSession: "正在同步课堂会话...",
    initializingBoard: "正在初始化白板...",
    clearSlideModalTitle: "清空当前页面板书？",
    clearSlideModalDescription: "此操作将清除当前幻灯片页面上的所有板书内容。",
    clearSlideConfirmLabel: "清空当前页",
    slideClearedToastTitle: "已清空当前页",
    slideClearedToastDesc: "当前页面上的所有板书已清空。",
    slowNetworkAria: "慢速网络连接提示",
    slowNetworkTitle: "网络连接较慢",
    slowNetworkDesc: "— 正在同步在线课堂会话，当前处于本地草稿模式。",
    retrySyncButton: "重试同步",
    toolErrorTitle: "白板遇到问题",
    toolErrorFallback: "运行此工具时发生意外错误。",
    toolReloadButton: "重新加载白板",
    dismissToastAria: "关闭通知",
  },
  hud: {
    appMenuAriaLabel: "应用菜单",
    boardMenuAriaLabel: "白板菜单",
    exportPng: "导出图片 (PNG)",
    exportPdf: "导出讲义 (PDF)",
    pdfScopeAriaLabel: "PDF 导出范围",
    pdfScopeAll: "全部课件",
    pdfScopeTab: "当前白板标签",
    pdfScopeSlide: "当前页面",
    findOnCanvas: "在画布中查找",
    help: "帮助",
    clearSlide: "清空当前页",
    paperStyleSectionTitle: "纸张与练字格 (Paper Style)",
    strokeSmoothingSectionTitle: "画笔平滑度",
    canvasBgSectionTitle: "画布背景色",
    canvasBgSwatchAria: (color: string) => `画布背景 ${color}`,
    customBgColorAria: "自定义背景色",
    paperPresets: {
      blank: "空白",
      "grid-small": "小方格",
      "grid-large": "大方格",
      dots: "点阵纸",
      lined: "横线纸",
      tianzige: "田字格",
      mizige: "米字格",
      "pinyin-tianzige": "拼音田字格",
    },
    prevSlideAria: "上一页",
    nextSlideAria: "下一页",
    addSlideAria: "新建页面",
    duplicateSlideAria: "复制页面",
    duplicateSlideTitle: "复制当前页面",
    deleteSlideAria: "删除当前页面",
    confirmDeleteSlideAria: "确认删除页面",
    confirmDeleteSlideLabel: "确认删除？",
    cancelDeleteSlideAria: "取消删除页面",
    cancelLabel: "取消",
    drawingToolbarAria: "绘图与教学素材工具栏",
    pointerSelectionLabel: "选择指针",
    pointerHandLabel: "抓手移动画布",
    shapesMenuAria: "选择几何图形",
    shapeTools: {
      rectangle: "矩形",
      diamond: "菱形",
      ellipse: "椭圆",
      arrow: "箭头",
      line: "直线",
    },
    freedrawAria: "画笔",
    freedrawTitle: "自由画笔 (P)",
    textAria: "文本",
    textTitle: "文本 (T)",
    eraserAria: "橡皮擦",
    eraserTitle: "橡皮擦 (E)",
    insertMenuTriggerAria: "插入图片、文档与视频",
    insertMenuTriggerTitle: "插入图片、PDF 或视频",
    insertPopoverAria: "插入教学素材与多媒体",
    insertImageLabel: "插入图片",
    pinyinAssistantTrigger: "拼音与汉字助手",
    stemTriggerAria: "STEM、数学与图形库工具",
    stemTriggerTitle: "LaTeX 公式、互动仿真与 STEM 图库",
    stemPopoverAria: "数学、仿真与 STEM 图库工具",
    stemLibraryTrigger: "教学素材与笔画图库",
    stemAllSubjects: "全部",
    stemGenerateSectionTitle: "按需生成",
    stemSidebarTitle: "图库",
    stemSidebarCloseAria: "关闭图库",
    stemCategoryLabels: {
      physics: { label: "物理", description: "电路图、光学、力学图示" },
      math: { label: "数学", description: "平面与立体几何、坐标系、函数" },
      chemistry: { label: "化学", description: "实验仪器、分子结构模型" },
      cs: { label: "计算机与流程图", description: "流程图、网络拓扑、系统架构" },
      chinese: {
        label: "中文教学 (田字格/笔画)",
        description: "田字格、米字格、永字八法基本笔画、汉字结构与声调符号",
      },
    },
    latexTrigger: "插入数学公式 (LaTeX)",
    simulationTrigger: "图像与几何",
    youtubeTrigger: "插入 YouTube 视频",
    laserAria: "激光笔",
    laserTitle: "激光笔 (K)",
    cursorChatTriggerAria: "光标实时聊天",
    cursorChatPlaceholder: "输入光标消息...",
    cursorChatHint: "Enter 固定 · Esc 关闭",
    remoteHostBadge: "教师",
    remoteOverflowBadge: (count: number) => `+${count} 人`,
    teacherPermissionsAria: "学生互动设置",
    screenFollowYou: "跟随你的画面",
    screenFreeMove: "自由移动",
    screenFollowingTeacher: "正在跟随教师",
    drawCustom: "指定学生",
    drawViewOnly: "仅查看",
    undoAria: "撤销",
    undoTitle: "撤销 (Ctrl+Z)",
    redoAria: "重做",
    redoTitle: "重做 (Ctrl+Y)",
    zoomOutAria: "缩小",
    zoomInAria: "放大",
    zoomMenuAria: "视图缩放选项",
    zoomMenuHeader: (percent: number) => `画布视图 (${percent}%)`,
    fitSlide: "适应整块画板",
    fitWidth: "适应画板宽度",
    zoom100Label: "缩放 100% (1:1)",
    pageNumberInputAria: "输入页码",
  },
  permissions: {
    teacherDialogTitle: "学生互动设置",
    teacherScreenSection: "学生画面模式",
    followYouBtn: "跟随你的画面",
    freeMoveBtn: "自由移动",
    teacherFollowHint: "学生将始终看到您当前浏览的画布区域，无法自行平移视角。",
    teacherFreeHint: "学生可以在画布上自由缩放和平移视角。",
    drawingPermissionSection: "学生画板权限",
    viewOnlyMode: "仅查看",
    customMode: "指定学生",
    viewOnlyHint: "仅您可以绘图，学生处于只读观看模式。",
    customModeHint: "选择可以绘图的学生。",
    searchParticipantPlaceholder: "搜索参与者",
    selectedCount: (editable: number, total: number) => `已选择: ${editable}/${total}`,
    deselectAllBtn: "取消全选",
    selectStudentAria: (name: string) => `选择 ${name}`,
  },
  pinyinModal: {
    title: "拼音与汉字生字卡助手",
    subtitle:
      "输入带数字声调的拼音（如 ni3 hao3、lv4）自动转换为声调符号，并一键生成田字格/米字格生字卡片。",
    modeCardLabel: "拼音 + 汉字练字格卡片 (田字格/米字格)",
    modeCardDesc: "在画布中央生成可自由移动、缩放的拼音四线格与汉字田字格组合卡片",
    modeTextLabel: "仅插入拼音 / 汉字文本",
    modeTextDesc: "在画布中央直接插入带声调的拼音与汉字文本元素",
    pinyinInputLabel: "拼音输入（支持数字 1-5 自动转声调，v 或 u: 转 ü）",
    pinyinInputPlaceholder: "例如：ni3 hao3, zhong1 wen2, lv4 se4...",
    convertedPreviewLabel: "声调转换预览",
    hanziInputLabel: "对应汉字（每字一格）",
    hanziInputPlaceholder: "例如：你好、中文...",
    gridStyleLabel: "练字格样式",
    gridTianzige: "田字格",
    gridMizige: "米字格",
    toneVowelsTitle: "声调韵母快捷输入（点击插入）",
    punctuationTitle: "中文标点符号",
    colorLabel: "字迹颜色",
    strokeColorLabels: {
      ink: "墨黑",
      white: "纯白",
      crimson: "绯红",
      blue: "宝蓝",
      emerald: "翠绿",
      amber: "琥珀黄",
    },
    copyPinyinButton: "复制拼音",
    copiedPinyinButton: "已复制！",
    cancelButton: "取消",
    insertCardButton: "插入生字卡片",
    insertTextButton: "插入拼音文本",
    practiceTitle: "课堂练习模式",
    practiceFull: "完整示范 (拼音+汉字)",
    practiceTrace: "描红练字 (示范+描红+空格)",
    practiceDictation: "听写默写 (仅显示拼音)",
    practicePinyinQuiz: "看字注音 (仅显示汉字)",
    gridJiugongge: "九宫格 (9-Palace)",
    gridSikexian: "四线三格 (纯拼音)",
    presetsTitle: "常用教学词汇、基本笔画与偏旁部首",
    cellsCountBadge: (count: number) => `${count} 格`,
  },
  modals: {
    cancel: "取消",
    close: "关闭",
    confirm: "确认",
    latexEditTitle: "编辑数学与科学公式 (LaTeX)",
    latexCloseTitle: "关闭对话框 (Esc)",
    latexChipInsertTitle: (latex: string) => `插入 ${latex}`,
    latexChips: {
      systemCases: "方程组",
      orBrackets: "或 (方括号)",
      derivativePrime: "导数 f'(x)",
      derivative: "微分 d/dx",
      partialDerivative: "偏导数",
      integral: "定积分",
      limit: "极限",
      sum: "求和 Sigma",
      matrix2x2: "2x2 矩阵",
      iff: "⇔ 等价",
      implies: "⇒ 推出",
      inSet: "∈ 属于",
      realSet: "ℝ 实数集",
      vector: "向量 AB",
      combination: "组合 C_n^k",
      subscript: "下标",
      delta: "Δ Delta",
      alpha: "α Alpha",
      beta: "β Beta",
      lambda: "λ Lambda",
      plusMinus: "± 正负",
      approx: "≈ 约等于",
      infinity: "∞ 无穷大",
    },
    latexInkColors: {
      defaultDark: "默认 (自适应白色)",
      defaultLight: "默认 (黑色)",
      amber: "琥珀黄",
      blue: "天蓝",
      emerald: "翠绿",
      rose: "玫红",
      purple: "紫罗兰",
    },
    latexInputPlaceholder: "例如: \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}",
    latexSyntaxWarning: "语法警告",
    latexEmptyPreviewHint: "在上方输入 LaTeX 公式以预览",
    latexInkColorLabel: "墨水颜色:",
    latexInsertingButton: "正在插入...",
    latexUpdateButton: "更新公式",
    latexInsertButton: "插入公式",
    simulationTitle: "函数图像与平面几何",
    simulationSubtitle: "选择直角坐标系图像画板或平面几何画板插入到课堂页面",
    simulationInsertButton: "插入到白板",
    studios: {
      graphing: {
        title: "函数图像与微积分",
        subtitle: "Oxy 直角坐标系",
        description:
          "在 Oxy 坐标平面绘制函数图像、不等式可行域、圆与椭圆。支持参数滑块、切线、积分面积着色以及零点与极值分析。",
        tags: ["函数图像", "不等式", "参数滑块", "切线分析", "极值与零点"],
      },
      geometry: {
        title: "平面几何",
        subtitle: "直观作图与动态拖拽",
        description:
          "提供点、线段、垂线、平行线、圆、多边形及教材标准几何图形模板，支持直接拖拽顶点观察几何关系。",
        tags: ["点与线段", "垂线与平行线", "圆", "多边形", "教材图形"],
      },
    },
    youtubeTitle: "插入 YouTube 视频",
    youtubeSubtitle: "嵌入支持全班同步播放控制的 YouTube 视频",
    youtubeUrlLabel: "YouTube 链接",
    youtubeUrlPlaceholder: "https://www.youtube.com/watch?v=... 或 https://youtu.be/...",
    youtubeInvalidUrlError: "链接格式无效。支持 watch、youtu.be、shorts、embed 格式。",
    youtubeDisplayTitleLabel: "显示标题",
    youtubePreviewLabel: "预览 (16:9)",
    youtubeStartTimeBadge: (seconds: number) => `起始: ${seconds}s`,
    youtubeInsertButton: "插入到白板",
    videoUploadTitle: "上传本地教学视频",
    videoUnsupportedError: "仅支持 MP4、WebM 视频（最大 250MB）",
    videoPresignFailedError: (status: number) => `无法初始化上传 (${status})`,
    videoUploadHttpFailedError: (status: number) => `上传至存储服务器失败 (HTTP ${status})`,
    videoUploadNetworkError: "上传视频至存储服务器时发生网络错误",
    mermaidTitle: "Mermaid 转教学图表",
    mermaidSubtitle: "在白板上直接生成流程图、时序图或架构图",
    mermaidCloseAria: "关闭",
    mermaidTemplatesLabel: "模板：",
    mermaidTemplateFlowchart: "流程图 (Flowchart)",
    mermaidTemplateSequence: "时序图 (Sequence)",
    mermaidTemplateClass: "类图 (Class)",
    mermaidSyntaxLabel: "Mermaid 语法：",
    mermaidKeyboardHint: "按 Ctrl + Enter 插入",
    mermaidEmptyError: "请输入 Mermaid 语法。",
    mermaidNoElementsError: "未能从此图表定义生成图形元素。",
    mermaidSyntaxError: (msg: string) => `Mermaid 语法错误: ${msg}`,
    mermaidSupportedDiagramsHint: "支持流程图、时序图、类图与状态图",
    mermaidCancelButton: "取消",
    mermaidProcessingButton: "正在处理...",
    mermaidInsertButton: "插入画板",
    webEmbedTitle: "嵌入学习网页",
    webEmbedSubtitle: "在画布中嵌入交互式网页或模拟实验",
    webEmbedCloseAria: "关闭",
    webEmbedUrlLabel: "网页链接 (URL https://...)",
    webEmbedUrlPlaceholder: "https://example.com/interactive-app",
    webEmbedNameLabel: "显示名称（可选）",
    webEmbedNamePlaceholder: "嵌入内容",
    webEmbedInvalidUrlError: "链接无效，请输入以 https:// 开头的网址",
    webEmbedCancelButton: "取消",
    webEmbedInsertButton: "插入画板",
    mathDockPlotToGraphTitle: "在函数图表板上绘制此表达式（若无则自动新建）",
    mathDockPlotToGraphButton: "绘制图表",
    mathDockStampToBoardTitle: "将此数学公式贴到白板上",
    mathDockStampToBoardButton: "贴到白板",
    mathDockCollapseTitle: "折叠为迷你控制栏",
    mathDockExpandTitle: "展开完整数学工具箱与教材例题",
    mathDockCollapseButton: "收起",
    mathDockExpandButton: "展开",
    mathDockDoneButton: "完成",
    mathDockTabAlgebra: "123 代数与数",
    mathDockTabFunctions: "f(x) 三角与指数",
    mathDockTabPresets: "★ 教材典型例题",
    mathDockTabCalculus: "∫∑ 微积分与 LaTeX",
    mathDockTemplates: {
      cubic_m: "三次函数（含参数 m）",
      biquadratic_m: "双二次函数",
      rational: "有理分式函数（渐近线）",
      parabola_m: "二次抛物线",
      trig_wave: "三角函数波形",
      radical: "根式函数",
      linear_ineq: "二元一次不等式区域",
      circle: "圆的方程",
      ellipse: "标准椭圆方程",
    },
  },
  widgets: {
    modeToggleToAnnotateTitle: "切换到批注绘制模式",
    modeToggleToInteractTitle: "切换到交互操作模式",
    interactLabel: "交互",
    annotateLabel: "批注",
    unlockWidgetTitle: "画板已锁定（点击解锁）",
    lockWidgetTitle: "锁定画板位置",
    lockedByTeacherTitle: "教师已锁定此画板",
    maximizeWidgetTitle: "最大化适配屏幕",
    restoreWidgetTitle: "还原先前视角",
    confirmDeleteWidgetTitle: "确认删除此画板",
    confirmDeleteWidgetLabel: "确认删除？",
    cancelDeleteWidgetTitle: "取消删除",
    cancelDeleteWidgetLabel: "取消",
    deleteWidgetAria: "删除画板",
    resizeHandleTitle: "拖拽调整大小",
    microPromptTouchPoint: "1. 点击基准线段或经过的点",
    microPromptPassThroughPoint: "2. 点击任意点或空白位置作线",
    microPromptPerpendicularEdge: "2. 点击目标线段作垂线",
    microPromptParallelEdge: "2. 点击目标线段作平行线",
    microPromptCancelTitle: "取消操作 (Esc)",
    openSlidersTitle: "展开参数滑块面板",
    parametersLabel: "参数",
    collapseSlidersTitle: "收起参数面板",
    collapseLabel: "收起",
    videoIndependentTitle: "当前为自主观看模式 - 点击同步跟随教师",
    videoFollowingTitle: "当前跟随教师同步播放 - 点击自主观看",
    videoIndependentLabel: "自主观看",
    videoFollowTeacherLabel: "跟随教师",
    videoResyncButton: "恢复跟随教师",
    videoResyncTitle: "恢复与教师同步播放",
    hidePlotTitle: "隐藏函数图像",
    showPlotTitle: "显示函数图像",
    hideInequalityTitle: "隐藏可行域",
    showInequalityTitle: "显示可行域",
    hideConicTitle: "隐藏圆锥曲线",
    showConicTitle: "显示圆锥曲线",
    inequalityShortTag: "不等式:",
    deleteExpressionTitle: (label: string) => `删除 ${label}`,
    invalidSyntaxLabel: "语法无效",
    mobileExpressionListTitle: "表达式与微积分列表",
    addButtonLabel: "添加",
    openMobileFormulaSheetTitle: "点击打开公式面板与触控键盘",
    emptyExpressionLabel: "(空)",
    openAlgebraDrawerTitle: "打开代数面板（表达式列表）",
    expressionListTitle: "表达式列表",
    collapseAlgebraDrawerTitle: "收起代数面板 [ ◀ ]",
    omniInputPlaceholder: "输入函数、不等式、圆或椭圆...",
    omniInputAria: "添加新表达式",
    clickToAddExpression: "点击输入函数 / 不等式...",
    newBadge: "+ 新建",
    addPointLabel: "+ 点",
    triangleAbcLabel: "三角形 ABC",
    triangleLabel: "三角形",
    segmentLabel: "线段",
    lineLabel: "直线",
    vectorLabel: "向量",
    perpendicularLabel: "垂线",
    parallelLabel: "平行线",
    circleLabel: "圆",
    polygonLabel: "多边形",
    clearGeometryBoardTitle: "清空几何画板",
    geometryPresets: {
      tri_altitudes: "三角形与三条高线",
      tri_medians: "三角形与三条中线",
      tri_circum: "三角形与外接圆",
      tri_in: "三角形与内切圆",
      tri_right: "直角三角形",
      tri_iso: "等腰三角形",
      tri_eq: "等边三角形",
      quad_para: "平行四边形",
      quad_rect: "矩形",
      quad_rhomb: "菱形",
      quad_sq: "正方形",
      circle_tan: "圆与两条切线",
      quad_inscribed: "圆内接四边形",
      poly_pentagon: "正五边形",
      quad_trapezoid: "等腰梯形",
    },
    extremumMaxBadge: (x: string, y: string) => `极大(${x}, ${y})`,
    extremumMinBadge: (x: string, y: string) => `极小(${x}, ${y})`,
    rootSweeperBadge: (paramName: string, val: string, count: number) =>
      `${paramName} = ${val} → ${count} 个根`,
    lpUnboundedReasons: {
      both_unbounded: "可行域无界 - 不存在最大值与最小值",
      max_unbounded: "不存在最大值（因可行域无界趋向 +∞）",
      min_unbounded: "不存在最小值（因可行域无界趋向 -∞）",
    },
    lpTableHeader: (fnExpr: string) => `优化 F(x, y) = ${fnExpr}`,
    lpVertexCol: "顶点",
    lpCoordsCol: "坐标",
    lpMaxSummary: (val: string, label: string, x: string, y: string) =>
      `★ max F = ${val} 于 ${label} (${x}, ${y})`,
    lpMinSummary: (val: string, label: string, x: string, y: string) =>
      `★ min F = ${val} 于 ${label} (${x}, ${y})`,
    stopwatchPauseTitle: "暂停仿真",
    stopwatchPlayTitle: "开始仿真",
    stopwatchResetTitle: "重置时间",
    riemannMethodLeft: "左端点",
    riemannMethodMidpoint: "中点",
    riemannMethodRight: "右端点",
    riemannMethodTrapezoidal: "梯形法",
    riemannExpandTitle: "点击展开黎曼和控制栏",
    riemannCollapseTitle: "收起黎曼控制栏",
    riemannCloseTitle: "关闭黎曼和",
    riemannFullTitle: "黎曼和",
    riemannDec5Title: "减少 5 个分割",
    riemannDec1Title: "减少 1 个分割",
    riemannInputTitle: "直接输入分割数 n (2 - 100)",
    riemannInc1Title: "增加 1 个分割",
    riemannInc5Title: "增加 5 个分割",
    sweeperExpandTitle: "点击展开 y = m 水平扫描控制栏",
    sweeperCollapseTitle: "收起 y = m 扫描控制栏",
    sweeperCloseTitle: "关闭 y = m 交点分析",
    sweeperFullTitle: "交点分析 y = m",
    sweeperInputTitle: "直接输入 m 的值",
    sweeperDec05Title: "减少 0.5",
    sweeperDec01Title: "减少 0.1",
    sweeperResetZeroTitle: "重置为 0",
    sweeperInc01Title: "增加 0.1",
    sweeperInc05Title: "增加 0.5",
    insituCircumcircleLabel: "外接圆",
    insituIncircleLabel: "内切圆",
    insituAltitudesLabel: "三条高",
    insituMediansLabel: "中线",
    insituBisectorsLabel: "角平分线",
    insituMeasurementsLabel: "测量",
    insituMidpointLabel: "中点 M",
    insituBisectorLabel: "中垂线 d",
    insituLengthLabel: "长度",
    insituCircleHeader: "圆",
    insituTwoTangentsLabel: (ptId: string) => `2 切线 (${ptId})`,
    insituCreateTangentsLabel: "作两条切线",
    insituPointLabelPrefix: "点:",
    insituVertexAltitudeTitle: "过此顶点的高线",
    insituVertexMedianTitle: "过此顶点的中线",
    insituVertexBisectorTitle: "过此顶点的角平分线",
    insituPolygonHeader: (count: number) => `多边形 (${count} 顶点)`,
    insituDeleteElementTitle: "删除元素",
    insituCloseTitle: "关闭",
    dockCompactTriggerTitle: "打开教学特征列表",
    dockCompactTriggerLabel: "特征",
    dockMenuHeader: "教学特征",
    dockMoreActionsTitle: "查看所有其他操作",
    dockMoreActionsLabel: (count: number) => `更多 (${count})`,
    dockAllActionsHeader: "所有操作",
    tangentOutOfDomainBadge: (x0: string) => `x₀ = ${x0} (定义域外)`,
    unmuteVideoLabel: "开启声音",
    snapIsoscelesBadge: "等腰",
    emptyFeasibleRegionNotice: "可行域为空",
    feasiblePointMemberBadge: (objValueStr: string) => `✓ 属于可行域${objValueStr}`,
    feasiblePointViolatedBadge: (expr: string) => `✕ 违反: ${expr}`,
    pedagogicalActions: {
      hatchingLabel: "斜线划除不可行域",
      hatchingTitle: "以教材斜线样式标示不等式组区域",
      objectiveOptimizerLabel: "优化目标函数 F",
      objectiveOptimizerTitle: "求目标函数 F(x, y) 的最大值与最小值",
      curveIntersectionsLabel: (plotName: string, count: number) =>
        count > 0 ? `${plotName} 的交点 (${count})` : `${plotName} 的交点`,
      curveIntersectionsTitle: (plotName: string) =>
        `求 ${plotName} 与其他图像的交点及坐标轴投影`,
      areaBetweenCurvesLabel: "两曲线间面积",
      areaBetweenCurvesTitle: "求由两曲线 f(x) 与 g(x) 所围成的平面面积",
      conicAxesLabel: (isEllipse: boolean) =>
        isEllipse ? "显示中心与顶点/轴" : "显示圆心与半径",
      conicAxesTitle: "显示圆锥曲线的中心、顶点与对称轴",
      conicFociLabel: "显示焦点",
      conicFociTitle: "显示椭圆的焦点 F₁, F₂ 与焦距",
      asymptotesLabel: "显示渐近线",
      asymptotesTitle: "显示垂直渐近线与水平渐近线",
      symmetryCenterLabel: "对称中心",
      symmetryCenterTitle: "显示图像的对称中心坐标",
      quadraticVertexAxisLabel: "显示顶点与对称轴",
      quadraticVertexAxisTitle: "抛物线的顶点坐标与对称轴",
      monotonicityLabel: "单调性与极值",
      monotonicityTitle: "显示单调区间与极值点",
      inflectionPointLabel: "拐点",
      inflectionPointTitle: "三次函数图像的拐点坐标",
      keyFeaturesLabel: "特征点",
      keyFeaturesTitle: "显示图像上的关键特征点",
      curveProbeLabel: "动点 M 坐标投影",
      curveProbeTitle: "曲线上可拖拽动点 M 及其在 Ox、Oy 轴上的投影",
      intervalAnalysisLabel: "区间 [a, b] 分析",
      intervalAnalysisTitle: "分析闭区间 [a, b] 上的最大值与最小值（可拖拽端点）",
      rootSweeperLabel: "交点分析 y = m",
      rootSweeperTitle: "利用水平扫描线分析方程 f(x) = m 的实根个数",
      tangentLineLabel: "x₀ 处切线",
      tangentLineTitle: "作曲线在点 x₀ 处的切线方程",
      riemannSumLabel: "黎曼和",
      riemannSumTitle: "使用黎曼积分和近似曲线下面积",
      definiteIntegralLabel: "定积分",
      definiteIntegralTitle: "计算函数的定积分值与几何面积 S",
      absFTransformLabel: "绝对值翻折 |f(x)|",
      absFTransformTitle: "保留 Ox 轴上方部分并将下方部分对称翻折至上方",
      fAbsTransformLabel: "偶对称变换 f(|x|)",
      fAbsTransformTitle: "保留 Oy 轴右侧部分并对称复制至左侧",
    },
  },
};
